import  { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: "Dr. Sarah Mitchell",
      role: "Dean of Academic Affairs",
      institution: "Riverside University College",
      location: "California, USA",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
      rating: 5,
      quote: `KnowTran transformed our institution from chaos to clarity. What used to take our finance team 40+ hours monthly for fee reconciliation now happens automatically in real-time. Our teachers have gotten their time back, and our students receive better attention because administrative burden has been reduced by 63%.`,
      metrics: {
        timeSaved: "40+ hours monthly",
        efficiency: "63% improvement",
        satisfaction: "98% staff satisfaction",
      },
      linkedinVerified: true,
      videoTestimonial: true,
    },
    {
      id: 2,
      name: "Michael Rodriguez",
      role: "Finance Head",
      institution: "St. Mary's International School",
      location: "London, UK",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      rating: 5,
      quote: `The month-end reconciliation nightmare is finally over. KnowTran's automated fee management has eliminated our spreadsheet chaos completely. We've achieved 2.5× faster reconciliation and our accuracy has improved to 99.8%. The ROI was evident within the first quarter itself.`,
      metrics: {
        reconciliation: "2.5× faster",
        accuracy: "99.8% accuracy",
        roi: "Positive ROI in Q1",
      },
      linkedinVerified: true,
      videoTestimonial: false,
    },
    {
      id: 3,
      name: "Prof. Amanda Chen",
      role: "Principal",
      institution: "Greenwood Academy",
      location: "Singapore",
      avatar: "https://randomuser.me/api/portraits/women/68.jpg",
      rating: 5,
      quote: `Our attendance accuracy jumped from 87% to 99.1% with KnowTran's mobile-first approach. Teachers love the one-tap attendance system, and parents receive instant notifications. The real-time analytics help us make data-driven decisions that directly impact student outcomes.`,
      metrics: {
        attendance: "99.1% accuracy",
        timePerClass: "30 seconds vs 20 minutes",
        parentSatisfaction: "96% satisfaction",
      },
      linkedinVerified: true,
      videoTestimonial: true,
    },
    {
      id: 4,
      name: "Dr. James Thompson",
      role: "IT Director",
      institution: "Metropolitan College",
      location: "Toronto, Canada",
      avatar: "https://randomuser.me/api/portraits/men/56.jpg",
      rating: 5,
      quote: `The security and compliance features gave us complete peace of mind. GDPR compliance, AWS infrastructure, and SSO integration made the transition seamless. Our IT team can focus on innovation instead of maintaining fragmented systems. The 99.9% uptime guarantee has been consistently met.`,
      metrics: {
        uptime: "99.9% achieved",
        security: "Zero breaches",
        integration: "Seamless SSO",
      },
      linkedinVerified: true,
      videoTestimonial: false,
    },
    {
      id: 5,
      name: "Lisa Park",
      role: "Administrative Manager",
      institution: "Sunrise Elementary School",
      location: "Sydney, Australia",
      avatar: "https://randomuser.me/api/portraits/women/25.jpg",
      rating: 5,
      quote: `KnowTran's dual-mode architecture perfectly adapted to our K-12 needs. The parent communication features and homework tracking have strengthened our school community. Implementation was smooth, and the support team guided us every step of the way.`,
      metrics: {
        implementation: "2 weeks setup",
        parentEngagement: "89% increase",
        efficiency: "71% admin reduction",
      },
      linkedinVerified: true,
      videoTestimonial: true,
    },
  ];

/*   const institutionLogos = [
    {
      name: "Riverside University",
      logo: "https://images.unsplash.com/photo-1562774053-701939374585?w=120&h=60&fit=crop&crop=center",
    },
    {
      name: "St. Mary's School",
      logo: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=120&h=60&fit=crop&crop=center",
    },
    {
      name: "Greenwood Academy",
      logo: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=120&h=60&fit=crop&crop=center",
    },
    {
      name: "Metropolitan College",
      logo: "https://images.unsplash.com/photo-1562774053-701939374585?w=120&h=60&fit=crop&crop=center",
    },
    {
      name: "Sunrise Elementary",
      logo: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=120&h=60&fit=crop&crop=center",
    },
    {
      name: "Oakwood High School",
      logo: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=120&h=60&fit=crop&crop=center",
    },
  ]; */

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  const currentTestimonial = testimonials?.[activeTestimonial];

  return (
    <section
      ref={sectionRef}
      className="py-14 sm:py-16 lg:py-20 bg-gradient-to-b from-surface to-background"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="inline-flex items-center space-x-2 bg-warning/10 text-warning px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-inter font-600 mb-4 sm:mb-6">
            <Icon name="Star" size={16} color="currentColor" />
            <span>Built by Educators, for Educators</span>
          </div>

          <h2 className="font-jakarta font-normal text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground mb-4 sm:mb-6">
            What Educational Leaders{" "}
            <span className="bg-gradient-to-r from-warning font-normal to-primary bg-clip-text text-transparent">
              Say About Us
            </span>
          </h2>

          <p className="font-inter font-500 text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Real stories from deans, principals, and administrators who
            transformed their institutions with KnowTran
          </p>
        </div>

        {/* Main Testimonial */}
        <div
          className={`transform transition-all duration-1000 delay-300 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-12 border-2 border-primary/20 shadow-2xl aurora-glow mb-10 sm:mb-12">
            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 items-center">
              {/* Left - Testimonial Content */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                {/* Quote */}
                <div className="relative">
                  <Icon
                    name="Quote"
                    size={40}
                    color="var(--color-primary)"
                    className="absolute -top-3 sm:-top-4 -left-2 opacity-20"
                  />
                  <blockquote className="font-inter font-500 text-base sm:text-lg lg:text-xl text-foreground leading-relaxed pl-6 sm:pl-8">
                    {currentTestimonial?.quote}
                  </blockquote>
                </div>

                {/* Metrics */}
                <div className="grid md:grid-cols-3 gap-3 sm:gap-4">
                  {Object.entries(currentTestimonial?.metrics)?.map(
                    ([key, value], _) => (
                      <div
                        key={key}
                        className="p-3 sm:p-4 bg-primary/5 rounded-xl border border-primary/20"
                      >
                        <div className="font-jakarta font-700 text-base sm:text-lg text-primary">
                          {value}
                        </div>
                        <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground capitalize">
                          {key?.replace(/([A-Z])/g, " $1")?.trim()}
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Author Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4 pt-5 sm:pt-6 border-t border-white/20">
                  <div className="relative">
                    <Image
                      src={currentTestimonial?.avatar}
                      alt={currentTestimonial?.name}
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover"
                    />
                    {currentTestimonial?.linkedinVerified && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <Icon
                          name="Check"
                          size={12}
                          color="white"
                          strokeWidth={3}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-0.5 sm:mb-1">
                      <h4 className="font-jakarta font-700 text-base sm:text-lg text-foreground">
                        {currentTestimonial?.name}
                      </h4>
                      {/* {currentTestimonial?.videoTestimonial && (
                        <div className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-inter font-600">
                          <Icon name="Video" size={12} color="currentColor" />
                          <span>Video</span>
                        </div>
                      )} */}
                    </div>
                    <div className="font-inter font-600 text-sm sm:text-base text-primary">
                      {currentTestimonial?.role}
                    </div>
                    <div className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                      {currentTestimonial?.institution} •{" "}
                      {currentTestimonial?.location}
                    </div>
                  </div>

                  {/* Rating - desktop */}
                  <div className="hidden sm:flex space-x-1 ml-auto">
                    {[...Array(currentTestimonial?.rating)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={20}
                        color="var(--color-warning)"
                        className="fill-[var(--color-warning)]"
                      />
                    ))}
                  </div>

                  {/* Rating - mobile */}
                  <div className="flex sm:hidden space-x-1">
                    {[...Array(currentTestimonial?.rating)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={14}
                        color="var(--color-warning)"
                        className="fill-[var(--color-warning)]"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right - Navigation & Controls */}
              <div className="space-y-4 sm:space-y-6">
                {/* Testimonial Navigation */}
                <div className="space-y-2.5 sm:space-y-3">
                  {testimonials?.map((testimonial, index) => (
                    <button
                      key={testimonial?.id}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-full flex items-center space-x-3 p-3.5 sm:p-4 rounded-xl transition-all duration-300 text-left ${
                        activeTestimonial === index
                          ? "bg-primary/10 border-2 border-primary/30"
                          : "bg-muted/30 border-2 border-transparent hover:bg-primary/5 hover:border-primary/20"
                      }`}
                    >
                      <Image
                        src={testimonial?.avatar}
                        alt={testimonial?.name}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-inter font-600 text-xs sm:text-sm text-foreground truncate">
                          {testimonial?.name}
                        </div>
                        <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground truncate">
                          {testimonial?.role}
                        </div>
                      </div>
                      {activeTestimonial === index && (
                        <Icon
                          name="ChevronRight"
                          size={16}
                          color="var(--color-primary)"
                        />
                      )}
                    </button>
                  ))}
                </div>

                {/* Auto-play Controls */}
                <div className="flex items-center justify-between p-3.5 sm:p-4 bg-muted/30 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="Play"
                      size={16}
                      color="var(--color-muted-foreground)"
                    />
                    <span className="font-inter font-500 text-xs sm:text-sm text-muted-foreground">
                      Auto-play
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    {testimonials?.map((_, index) => (
                      <div
                        key={index}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === activeTestimonial
                            ? "bg-primary w-5 sm:w-6"
                            : "bg-muted w-2"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Institution Logos */}
        {/* <div
          className={`transform transition-all duration-1000 delay-500 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        > */}
        {/* <div className="text-center mb-8">
            <h3 className="font-jakarta font-700 text-xl text-foreground mb-2">
              Trusted by Progressive Educational Institutions
            </h3>
            <p className="font-inter font-500 text-muted-foreground">
              Join 2,800+ schools and colleges worldwide
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {institutionLogos?.map((institution, index) => (
              <div
                key={index}
                className="flex items-center justify-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/20 hover:shadow-lg transition-all duration-300 aurora-glass"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Image
                  src={institution?.logo}
                  alt={institution?.name}
                  className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div> */}
        {/* </div> */}

        {/* Social Proof Metrics */}
        <div
          className={`mt-14 sm:mt-16 transform transition-all duration-1000 delay-700 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl p-6 sm:p-8 border border-primary/20">
            <div className="grid md:grid-cols-4 gap-6 sm:gap-8 text-center">
              <div>
                <div className="font-jakarta font-800 text-lg sm:text-2xl text-primary mb-1.5 sm:mb-2">
                  Secure by Default
                </div>
                <div className="font-inter font-600 text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  HTTPS/TLS Enabled
                </div>
                <div className="font-inter font-400 text-xs sm:text-sm text-muted-foreground">
                  Encryption in transit
                </div>
              </div>
              <div>
                <div className="font-jakarta font-800 text-lg sm:text-2xl text-success mb-1.5 sm:mb-2">
                  Built on AWS
                </div>
                <div className="font-inter font-600 text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  Modern Cloud Infrastructure
                </div>
                <div className="font-inter font-400 text-xs sm:text-sm text-muted-foreground">
                  Scalable and globally available
                </div>
              </div>
              <div>
                <div className="font-jakarta font-800 text-lg sm:text-2xl text-warning mb-1.5 sm:mb-2">
                  Onboarding & Support
                </div>
                <div className="font-inter font-600 text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  Guided Rollout
                </div>
                <div className="font-inter font-400 text-xs sm:text-sm text-muted-foreground">
                  Timeline tailored to your needs
                </div>
              </div>
              <div>
                <div className="font-jakarta font-800 text-lg sm:text-2xl text-accent mb-1.5 sm:mb-2">
                  Roadmap
                </div>
                <div className="font-inter font-600 text-sm sm:text-base text-foreground mb-0.5 sm:mb-1">
                  SSO & Monitoring
                </div>
                <div className="font-inter font-400 text-xs sm:text-sm text-muted-foreground">
                  Planned post‑launch
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Video Testimonials CTA */}
        {/* <div
          className={`text-center mt-16 transform transition-all duration-1000 delay-900 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 aurora-glass">
            <h3 className="font-jakarta font-700 text-2xl text-foreground mb-4">
              See More Success Stories
            </h3>
            <p className="font-inter font-500 text-muted-foreground mb-6 max-w-2xl mx-auto">
              Watch video testimonials from educational leaders who transformed
              their institutions with KnowTran
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center space-x-2 bg-primary text-white px-8 py-4 rounded-xl font-jakarta font-600 text-lg hover:bg-primary/90 transition-all duration-300 aurora-glow">
                <Icon name="Play" size={20} color="white" />
                <span>Watch Video Testimonials</span>
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("demo-booking")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center space-x-2 bg-white text-primary px-8 py-4 rounded-xl font-jakarta font-600 text-lg border-2 border-primary/20 hover:bg-primary/5 transition-all duration-300"
              >
                <Icon name="Calendar" size={20} color="currentColor" />
                <span>Book Your Demo</span>
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default TestimonialsSection;
